import React from "react";
import ExerciseGuide from "./ExerciseGuide";
import { TYPE_COLORS } from "../data/constants";
import { DAY_EXERCISES } from "../data/exerciseConfig";

/**
 * Tarjeta expandible para un día de entrenamiento.
 * @param {object} day - Datos del día (del trainingPlan).
 * @param {boolean} isExpanded - Si está desplegada.
 * @param {boolean} isChecked - Si está marcada como completada.
 * @param {function} onToggle - Callback para expandir/colapsar.
 * @param {function} onCheck - Callback para marcar completado.
 */
export default function DayCard({ day, isExpanded, isChecked, onToggle, onCheck }) {
  const colors = TYPE_COLORS[day.type] || TYPE_COLORS["Carrera"];
  const exercises = DAY_EXERCISES[day.day] || [];

  return (
    <div
      style={{
        background: isChecked ? "var(--bg-secondary, #F9FAFB)" : "var(--bg-color, #fff)",
        border: `1.5px solid ${isExpanded ? colors.dot : "var(--border-color, #E5E7EB)"}`,
        borderRadius: 14,
        overflow: "hidden",
        opacity: isChecked ? 0.6 : 1,
        transition: "all 0.2s ease",
        boxShadow: isExpanded ? `0 4px 20px ${colors.dot}22` : "none",
      }}
    >
      {/* Fila principal */}
      <div
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          cursor: "pointer",
        }}
      >
        {/* Checkbox */}
        <div
          onClick={(e) => { e.stopPropagation(); onCheck(); }}
          style={{
            width: 22,
            height: 22,
            borderRadius: 7,
            flexShrink: 0,
            border: isChecked ? "none" : `2px solid ${colors.dot}88`,
            background: isChecked ? colors.dot : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          {isChecked && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>

        {/* Fecha */}
        <div style={{ width: 72, flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-color, #111)" }}>
            {day.date.split(" ").slice(0, 2).join(" ")}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-color-secondary, #9CA3AF)" }}>
            {day.date.split(" ").slice(2).join(" ")}
          </div>
        </div>

        {/* Badge de tipo */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "3px 9px",
            borderRadius: 99,
            background: colors.bg,
            color: colors.text,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {day.type}
        </div>

        {/* Actividad */}
        <div
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-color, #374151)",
            textDecoration: isChecked ? "line-through" : "none",
          }}
        >
          {day.activity}
        </div>

        {/* Kilómetros */}
        {day.km !== "—" && (
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.dot, flexShrink: 0 }}>
            {day.km}
          </div>
        )}

        {/* Indicador de intensidad */}
        <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              style={{
                width: 5,
                height: 14,
                borderRadius: 2,
                background: n <= day.intensity ? colors.dot : "var(--border-color, #E5E7EB)",
                transition: "background 0.2s",
              }}
            />
          ))}
        </div>

        {/* Flecha */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--text-color-secondary, #9CA3AF)"
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* Detalle expandido */}
      {isExpanded && (
        <div className="fade-in" style={{ padding: "0 14px 14px 48px" }}>
          <div
            style={{
              fontSize: 13,
              color: "var(--text-color, #374151)",
              lineHeight: 1.6,
              marginBottom: 8,
            }}
          >
            <strong style={{ color: colors.text }}>Rutina:</strong> {day.detail}
          </div>

          <div
            style={{
              fontSize: 12,
              color: colors.text,
              background: colors.bg,
              padding: "8px 12px",
              borderRadius: 10,
              lineHeight: 1.5,
              borderLeft: `3px solid ${colors.dot}`,
            }}
          >
            💡 {day.tip}
          </div>

          {/* Guías visuales */}
          <ExerciseGuide exercises={exercises} colors={colors} />
        </div>
      )}
    </div>
  );
}
